import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Box,
  Alert,
  Snackbar,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  CircularProgress,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { Computer, MeetingRoom, Search, People, Info } from '@mui/icons-material';
import Chip from '@mui/material/Chip';
import RecurrencePattern from './RecurrencePattern';
import SeriesPreview from './SeriesPreview';
import DeskFloorPlan from './DeskFloorPlan';
import { createBooking, getBooking, updateBooking } from '../services/bookingService';
import { previewSeries, createSeries } from '../services/seriesService';
import { getAllRooms } from '../services/roomService';
import { getAllClinics } from '../services/clinicService';
import { formatDate } from '../utils/rotorHelper';
import { getRecurrenceDescription } from '../utils/recurrenceHelper';
import { format, addWeeks } from 'date-fns';
import api from '../services/api';

function BookingForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(id);

  const [resourceType, setResourceType] = useState(searchParams.get('type') || 'room');
  const [isRecurring, setIsRecurring] = useState(false);
  const [rosieClinicId, setRosieClinicId] = useState(null);
  const [resources, setResources] = useState([]);

  // Desk floor plan state
  const [deskStep, setDeskStep] = useState('form'); // 'form' | 'floorplan'
  const [deskAvailability, setDeskAvailability] = useState({});
  const [selectedDesk, setSelectedDesk] = useState(null);
  const [deskSearchLoading, setDeskSearchLoading] = useState(false);

  const defaultStart = new Date();
  defaultStart.setHours(9, 0, 0, 0);
  const defaultEnd = new Date();
  defaultEnd.setHours(10, 0, 0, 0);

  const [formData, setFormData] = useState({
    room_id: '',
    start_time: defaultStart,
    end_time: defaultEnd,
    staff_name: '',
    purpose: '',
    notes: '',
    color: '#B082E4',
    booking_date: new Date(),
    series_name: '',
    recurrence_type: 'weekly',
    recurrence_pattern: null,
    series_start_date: new Date(),
    series_end_date: null,
  });

  const selectedRoom = resources.find(r => r.id === formData.room_id) || null;
  const selectedRoomEquipment = selectedRoom?.equipment
    ? (typeof selectedRoom.equipment === 'string' ? JSON.parse(selectedRoom.equipment) : selectedRoom.equipment)
    : [];

  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState('');
  const [conflictDetails, setConflictDetails] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [requesterEmail, setRequesterEmail] = useState('');
  const [adHocDialog, setAdHocDialog] = useState({ open: false });

  // Load Rosie clinic
  useEffect(() => {
    getAllClinics(true).then(clinics => {
      const rosie = clinics.find(c => c.clinic_code === 'ROSIE') || clinics[0];
      if (rosie) setRosieClinicId(rosie.id);
    }).catch(console.error);
  }, []);

  // Load resources for room type
  useEffect(() => {
    if (!rosieClinicId || resourceType !== 'room') return;
    getAllRooms({ clinic_id: rosieClinicId, resource_type: 'room', is_active: 'true' })
      .then(setResources)
      .catch(console.error);
    setFormData(prev => ({ ...prev, room_id: '' }));
  }, [resourceType, rosieClinicId]);

  // Reset desk state when switching to room
  useEffect(() => {
    if (resourceType === 'room') {
      setDeskStep('form');
      setSelectedDesk(null);
      setDeskAvailability({});
    }
  }, [resourceType]);

  // For desk recurring bookings, default end date to 4 weeks from today
  useEffect(() => {
    if (resourceType === 'desk' && isRecurring) {
      setFormData(prev => ({
        ...prev,
        series_end_date: prev.series_end_date || addWeeks(new Date(), 4),
      }));
    }
  }, [resourceType, isRecurring]);

  // Load booking data in edit mode
  useEffect(() => {
    if (!isEditMode || !id) return;
    const load = async () => {
      try {
        const booking = await getBooking(id);
        const st = new Date();
        const [sh, sm] = (booking.start_time || '09:00').split(':');
        st.setHours(parseInt(sh), parseInt(sm), 0, 0);
        const et = new Date();
        const [eh, em] = (booking.end_time || '10:00').split(':');
        et.setHours(parseInt(eh), parseInt(em), 0, 0);

        setFormData(prev => ({
          ...prev,
          room_id: booking.room_id || '',
          booking_date: new Date(booking.booking_date),
          start_time: st,
          end_time: et,
          staff_name: booking.doctor_name || '',
          purpose: booking.purpose || '',
          notes: booking.notes || '',
          color: booking.color || '#B082E4',
        }));

        if (booking.clinic_id) {
          getAllRooms({ clinic_id: booking.clinic_id, is_active: 'true' })
            .then(setResources)
            .catch(console.error);
        }
      } catch {
        setError('Failed to load booking data');
      } finally {
        setInitialLoading(false);
      }
    };
    load();
  }, [isEditMode, id]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handlePatternChange = (recurrenceType, pattern) => {
    setFormData(prev => ({ ...prev, recurrence_type: recurrenceType, recurrence_pattern: pattern }));
  };

  const buildPayload = () => ({
    clinic_id: rosieClinicId,
    room_id: formData.room_id,
    booking_date: formatDate(formData.booking_date),
    start_time: format(formData.start_time, 'HH:mm'),
    end_time: format(formData.end_time, 'HH:mm'),
    session: 'all_day',
    doctor_name: formData.staff_name,
    purpose: formData.purpose,
    notes: formData.notes,
    color: formData.color,
  });

  // Core booking submission (called from both room form and desk confirm)
  const submitOneTimeBooking = async () => {
    setError('');
    setConflictDetails(null);
    setLoading(true);
    try {
      const payload = buildPayload();
      if (isEditMode) {
        await updateBooking(id, payload);
        setSuccess(true);
        setTimeout(() => navigate('/bookings'), 2000);
      } else {
        await createBooking(payload);
        setSuccess(true);
        setTimeout(() => navigate(`/calendar?date=${payload.booking_date}`), 1500);
      }
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.conflicts) {
        setError('This time slot is already booked');
        setConflictDetails(errorData.conflicts);
        // If on floor plan, go back so user can re-search
        if (deskStep === 'floorplan') setDeskStep('form');
      } else {
        setError(errorData?.error || `Failed to ${isEditMode ? 'update' : 'create'} booking`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitOneTime = (e) => {
    e.preventDefault();
    submitOneTimeBooking();
  };

  const handlePreview = async () => {
    setError('');
    if (!formData.recurrence_pattern) { setError('Please configure the recurrence pattern'); return; }
    try {
      const base = buildPayload();
      const seriesData = {
        ...base,
        series_name: formData.series_name,
        recurrence_type: formData.recurrence_type,
        recurrence_pattern: formData.recurrence_pattern,
        series_start_date: formatDate(formData.series_start_date),
        series_end_date: formData.series_end_date ? formatDate(formData.series_end_date) : null,
      };
      const preview = await previewSeries(seriesData);
      setPreviewData(preview);
      setShowPreview(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate preview');
    }
  };

  const handleCreateSeries = async (excludedDates = []) => {
    setLoading(true);
    setError('');
    setConflictDetails(null);
    try {
      const base = buildPayload();
      const seriesData = {
        ...base,
        series_name: formData.series_name,
        recurrence_type: formData.recurrence_type,
        recurrence_pattern: formData.recurrence_pattern,
        series_start_date: formatDate(formData.series_start_date),
        series_end_date: formData.series_end_date ? formatDate(formData.series_end_date) : null,
        excluded_dates: excludedDates,
      };
      await createSeries(seriesData);
      setShowPreview(false);
      setSuccess(true);
      const firstDate = previewData?.instances?.find(
        inst => !excludedDates.includes(inst.booking_date)
      )?.booking_date || seriesData.series_start_date;
      setTimeout(() => navigate(`/calendar?date=${firstDate}`), 1500);
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.conflicts) {
        setError(errorData.message || 'Booking conflicts detected');
        setConflictDetails(errorData.conflicts);
        if (deskStep === 'floorplan') setDeskStep('form');
      } else {
        setError(errorData?.error || 'Failed to create booking series');
      }
      setShowPreview(false);
    } finally {
      setLoading(false);
    }
  };

  // ── DESK FLOW ──────────────────────────────────────────────────────────────

  const handleFindAvailableDesks = async () => {
    setError('');
    const searchDate = isRecurring ? formData.series_start_date : formData.booking_date;
    if (!searchDate || !formData.start_time || !formData.end_time) {
      setError('Please enter date and times before searching');
      return;
    }
    const startStr = format(formData.start_time, 'HH:mm');
    const endStr = format(formData.end_time, 'HH:mm');
    if (startStr >= endStr) {
      setError('End time must be after start time');
      return;
    }

    setDeskSearchLoading(true);
    try {
      const res = await api.get('/rooms/desk-availability', {
        params: {
          date: formatDate(searchDate),
          start_time: startStr,
          end_time: endStr,
        },
      });

      const map = {};
      res.data.forEach(room => {
        const num = parseInt(room.room_number.replace(/\D/g, ''), 10);
        if (num) {
          map[num] = {
            id: room.id,
            room_name: room.room_name,
            equipment: room.equipment
              ? (typeof room.equipment === 'string' ? JSON.parse(room.equipment) : room.equipment)
              : [],
            available: room.available,
          };
        }
      });

      setDeskAvailability(map);
      setSelectedDesk(null);
      handleChange('room_id', '');
      setDeskStep('floorplan');
    } catch (err) {
      setError('Failed to check desk availability. Please try again.');
    } finally {
      setDeskSearchLoading(false);
    }
  };

  const handleDeskClick = (deskNum) => {
    if (deskNum === null) {
      setSelectedDesk(null);
      handleChange('room_id', '');
      return;
    }
    setSelectedDesk(deskNum);
    handleChange('room_id', deskAvailability[deskNum].id);
  };

  const handleDeskConfirm = () => {
    if (isRecurring) {
      handlePreview();
    } else {
      submitOneTimeBooking();
    }
  };

  const handleBackToForm = () => {
    setDeskStep('form');
    setSelectedDesk(null);
    handleChange('room_id', '');
  };

  // ── HELPERS ────────────────────────────────────────────────────────────────

  const bookingSummary = {
    date: isRecurring
      ? `From ${formatDate(formData.series_start_date)}`
      : formatDate(formData.booking_date),
    start: formData.start_time ? format(formData.start_time, 'HH:mm') : '–',
    end: formData.end_time ? format(formData.end_time, 'HH:mm') : '–',
    name: formData.staff_name,
    purpose: formData.purpose,
  };

  const errorBlock = error && (
    <Grid item xs={12}>
      <Alert severity="error">
        <Typography variant="body1" sx={{ fontWeight: 'bold', mb: conflictDetails ? 1 : 0 }}>{error}</Typography>
        {conflictDetails && conflictDetails.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {isRecurring ? (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>Conflicting dates:</Typography>
                {conflictDetails.slice(0, 10).map((c, i) => (
                  <Typography key={i} variant="body2" sx={{ ml: 2 }}>
                    • {new Date(c.date).toLocaleDateString('en-GB')} ({c.start_time}–{c.end_time})
                  </Typography>
                ))}
                {conflictDetails.length > 10 && (
                  <Typography variant="body2" sx={{ ml: 2, fontStyle: 'italic' }}>
                    … and {conflictDetails.length - 10} more
                  </Typography>
                )}
              </>
            ) : (
              conflictDetails.map((c, i) => (
                <Typography key={i} variant="body2" sx={{ ml: 2 }}>
                  • {c.doctor_name || 'Existing booking'} ({c.start_time}–{c.end_time})
                </Typography>
              ))
            )}
          </Box>
        )}
      </Alert>
    </Grid>
  );

  if (initialLoading) {
    return <Container maxWidth="md"><Typography variant="h4">Loading…</Typography></Container>;
  }

  // ── RENDER: DESK FLOOR PLAN STEP ──────────────────────────────────────────
  if (resourceType === 'desk' && deskStep === 'floorplan') {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" gutterBottom>Book Desk Space</Typography>
          <Typography variant="body2" color="text.secondary">
            Select an available desk from the floor plan below
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
        )}

        <DeskFloorPlan
          deskData={deskAvailability}
          selectedDeskNum={selectedDesk}
          onDeskClick={handleDeskClick}
          onConfirm={handleDeskConfirm}
          onBack={handleBackToForm}
          bookingSummary={bookingSummary}
          isRecurring={isRecurring}
          loading={loading}
        />

        <SeriesPreview
          open={showPreview}
          onClose={() => setShowPreview(false)}
          previewData={previewData}
          onConfirm={handleCreateSeries}
          loading={loading}
        />

        <Snackbar
          open={success}
          autoHideDuration={2000}
          message={isRecurring ? 'Recurring booking series created!' : 'Booking created successfully!'}
        />
      </Container>
    );
  }

  // ── RENDER: FORM (both desk step-1 and room booking) ──────────────────────
  const isDeskForm = resourceType === 'desk';
  const resourceLabel = isDeskForm ? 'Desk Space' : 'Room';

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {isEditMode ? 'Edit Booking' : `Book ${resourceLabel}`}
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <form onSubmit={
            isDeskForm
              ? (e) => { e.preventDefault(); handleFindAvailableDesks(); }
              : isRecurring
                ? (e) => { e.preventDefault(); handlePreview(); }
                : handleSubmitOneTime
          }>
            <Grid container spacing={3}>

              {/* Resource type selector */}
              {!isEditMode && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    What would you like to book?
                  </Typography>
                  <ToggleButtonGroup
                    value={resourceType}
                    exclusive
                    onChange={(e, v) => v && setResourceType(v)}
                    size="large"
                  >
                    <ToggleButton value="desk" sx={{ px: 3 }}>
                      <Computer sx={{ mr: 1 }} /> Desk Space
                    </ToggleButton>
                    <ToggleButton value="room" sx={{ px: 3 }}>
                      <MeetingRoom sx={{ mr: 1 }} /> Room
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              )}

              {/* Room selector — rooms only */}
              {!isDeskForm && (
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Select Room"
                    fullWidth
                    required
                    value={formData.room_id}
                    onChange={(e) => handleChange('room_id', e.target.value)}
                  >
                    {resources.length === 0 && <MenuItem value="" disabled>Loading…</MenuItem>}
                    {resources.map((r) => (
                      <MenuItem key={r.id} value={r.id}>{r.room_name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {/* Room details panel */}
              {!isDeskForm && selectedRoom && (
                <Grid item xs={12}>
                  <Box sx={{
                    p: 2,
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'flex-start',
                  }}>
                    {selectedRoom.photo_url && (
                      <Box
                        component="img"
                        src={selectedRoom.photo_url}
                        alt={selectedRoom.room_name}
                        sx={{
                          width: 160,
                          height: 100,
                          objectFit: 'cover',
                          borderRadius: 1,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 160 }}>
                      <Info fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {selectedRoom.description}
                      </Typography>
                    </Box>
                    {selectedRoom.capacity && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <People fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          Capacity: {selectedRoom.capacity}
                        </Typography>
                      </Box>
                    )}
                    {selectedRoomEquipment.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selectedRoomEquipment.map((item, i) => (
                          <Chip key={i} label={item} size="small" variant="outlined" />
                        ))}
                      </Box>
                    )}
                    </Box>
                  </Box>
                </Grid>
              )}

              {/* Staff name */}
              <Grid item xs={12} md={isDeskForm ? 6 : 6}>
                <TextField
                  label="Name (staff member / clinician)"
                  fullWidth
                  value={formData.staff_name}
                  onChange={(e) => handleChange('staff_name', e.target.value)}
                  placeholder="Who is this booking for?"
                />
              </Grid>

              {/* Times */}
              <Grid item xs={12} md={6}>
                <TimePicker
                  label="Start Time"
                  value={formData.start_time}
                  onChange={(v) => handleChange('start_time', v)}
                  ampm={false}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TimePicker
                  label="End Time"
                  value={formData.end_time}
                  onChange={(v) => handleChange('end_time', v)}
                  ampm={false}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>

              {/* Purpose */}
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Purpose"
                  fullWidth
                  value={formData.purpose}
                  onChange={(e) => handleChange('purpose', e.target.value)}
                >
                  {[
                    'Clinic',
                    'Supervision',
                    'Staff Training & Education',
                    'Patient Group',
                    'Internal Meeting',
                    'External Meeting',
                    'Online Meeting',
                    'Admin Session',
                    'Other (please add in notes)',
                  ].map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Colour */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Booking Colour</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => handleChange('color', e.target.value)}
                    style={{ width: 60, height: 40, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
                  />
                  <TextField
                    value={formData.color}
                    onChange={(e) => handleChange('color', e.target.value)}
                    size="small"
                    sx={{ width: 120 }}
                  />
                </Box>
              </Grid>

              {/* Notes */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Notes"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Additional notes or requirements"
                />
              </Grid>

              {/* Recurring toggle */}
              {!isEditMode && (
                <Grid item xs={12}>
                  <Divider sx={{ mb: 2 }} />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isRecurring}
                        onChange={(e) => setIsRecurring(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={<Typography variant="subtitle1" fontWeight="medium">This is a recurring booking</Typography>}
                  />
                </Grid>
              )}

              {/* ONE-TIME: date picker */}
              {!isRecurring && (
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Booking Date"
                    value={formData.booking_date}
                    onChange={(v) => handleChange('booking_date', v)}
                    format="dd/MM/yyyy"
                    maxDate={isDeskForm ? addWeeks(new Date(), 4) : undefined}
                    slotProps={{ textField: { fullWidth: true, required: true, helperText: isDeskForm ? 'Desk bookings limited to 4 weeks in advance' : undefined } }}
                  />
                </Grid>
              )}

              {/* RECURRING: series fields */}
              {isRecurring && !isEditMode && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      label="Series Name (Optional)"
                      fullWidth
                      value={formData.series_name}
                      onChange={(e) => handleChange('series_name', e.target.value)}
                      placeholder="e.g., Dr. Smith weekly sessions"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Series Start Date"
                      value={formData.series_start_date}
                      onChange={(v) => handleChange('series_start_date', v)}
                      format="dd/MM/yyyy"
                      maxDate={isDeskForm ? addWeeks(new Date(), 4) : undefined}
                      slotProps={{ textField: { fullWidth: true, required: true, helperText: isDeskForm ? 'Desk bookings limited to 4 weeks in advance' : undefined } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Series End Date (Optional)"
                      value={formData.series_end_date}
                      onChange={(v) => handleChange('series_end_date', v)}
                      format="dd/MM/yyyy"
                      maxDate={isDeskForm ? addWeeks(new Date(), 4) : undefined}
                      slotProps={{ textField: { fullWidth: true, helperText: isDeskForm ? 'Desk bookings limited to 4 weeks in advance' : 'Leave blank for indefinite' } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <RecurrencePattern
                      recurrenceType={formData.recurrence_type}
                      pattern={formData.recurrence_pattern}
                      onChange={handlePatternChange}
                    />
                  </Grid>
                  {formData.recurrence_pattern && (
                    <Grid item xs={12}>
                      <Alert severity="info">
                        <strong>Pattern:</strong>{' '}
                        {getRecurrenceDescription(formData.recurrence_type, formData.recurrence_pattern)}
                      </Alert>
                    </Grid>
                  )}
                </>
              )}

              {errorBlock}

              {/* Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button variant="outlined" onClick={() => navigate('/bookings')} disabled={loading || deskSearchLoading}>
                    Cancel
                  </Button>

                  {isDeskForm ? (
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={deskSearchLoading ? <CircularProgress size={18} color="inherit" /> : <Search />}
                      disabled={deskSearchLoading || !formData.start_time || !formData.end_time}
                    >
                      {deskSearchLoading ? 'Checking availability…' : 'Find Available Desks'}
                    </Button>
                  ) : isRecurring ? (
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={!formData.room_id || !formData.recurrence_pattern || loading}
                    >
                      Preview Instances
                    </Button>
                  ) : (
                    <Button type="submit" variant="contained" disabled={loading || !formData.room_id}>
                      {loading
                        ? (isEditMode ? 'Updating…' : 'Creating…')
                        : (isEditMode ? 'Update Booking' : 'Create Booking')}
                    </Button>
                  )}
                </Box>
              </Grid>

            </Grid>
          </form>
        </CardContent>
      </Card>

      <SeriesPreview
        open={showPreview}
        onClose={() => setShowPreview(false)}
        previewData={previewData}
        onConfirm={handleCreateSeries}
        loading={loading}
      />

      <Snackbar
        open={success}
        autoHideDuration={2000}
        message={
          isEditMode
            ? 'Booking updated successfully!'
            : isRecurring
            ? 'Recurring booking series created!'
            : 'Booking created successfully!'
        }
      />

      <Dialog open={adHocDialog.open} onClose={() => setAdHocDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Send confirmation email?</DialogTitle>
        <DialogContent>
          <TextField
            label="Email address"
            type="email"
            fullWidth
            value={requesterEmail}
            onChange={e => setRequesterEmail(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdHocDialog({ open: false })} color="inherit">Skip</Button>
          <Button onClick={() => setAdHocDialog({ open: false })} variant="contained" disabled={!requesterEmail}>Send</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default BookingForm;
